/**
 * One chat turn. User turns are plain text; assistant turns are an ordered list of `parts` —
 * plain-text segments, narrations (always shown, styled as an ordinary message) and, when the
 * caller asks for them, tool calls — so each renders inline at the chronological point it
 * happened, not in a side panel. The agent replies in plain prose with no markdown syntax, so
 * text segments render as-is with `whitespace-pre-wrap` — no parser, and no risk of ever
 * rendering the reply as HTML.
 */

import type { NarrationRecord, ToolCallRecord } from "@/lib/api";
import { cn } from "@/lib/utils";

import { NarrationBubble } from "./narration-bubble";
import { ToolCallRow } from "./tool-call-row";

export type ChatMessagePart =
  | { kind: "text"; text: string }
  | { kind: "tool_call"; toolCall: ToolCallRecord }
  | { kind: "narration"; narration: NarrationRecord };

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Assistant only — chronological text/tool-call segments. Falls back to a single text part
   *  built from `content` when absent (e.g. a bare status update before the first part exists). */
  parts?: ChatMessagePart[];
  /** Still receiving events — shows a cursor after the last text segment. */
  streaming?: boolean;
}

/**
 * Drop the parts that won't render, then rejoin the text they were splitting.
 *
 * `partsFromMessage` splits the reply at every marker's `text_offset`, including tool calls.
 * With the tool-call toggle off (the default) those markers render nothing — but the split
 * they caused remained, so the reply appeared as two separate bubbles broken at the offset,
 * mid-sentence and often mid-word ("Here is what I found. T" / "he slow-moving Dx cohort…").
 * It stayed invisible while the agent happened to call every tool before writing any text
 * (all offsets 0, nothing to split); narrating once per phase of work puts markers in the
 * middle of the reply, which is exactly where it would have started mangling the answer.
 *
 * Narrations are never dropped — they are meant to interrupt the reply — so a text split
 * around one is real and is preserved.
 */
function visibleParts(parts: ChatMessagePart[], showToolCalls: boolean): ChatMessagePart[] {
  const kept = showToolCalls ? parts : parts.filter((part) => part.kind !== "tool_call");
  return kept.reduce<ChatMessagePart[]>((acc, part) => {
    const previous = acc[acc.length - 1];
    if (part.kind === "text" && previous?.kind === "text") {
      acc[acc.length - 1] = { kind: "text", text: previous.text + part.text };
      return acc;
    }
    acc.push(part);
    return acc;
  }, []);
}

export function MessageBubble({
  message,
  showToolCalls,
}: {
  message: ChatMessage;
  showToolCalls: boolean;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-primary text-primary-foreground max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed">
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }

  const rawParts: ChatMessagePart[] =
    message.parts ??
    (message.content || message.streaming ? [{ kind: "text", text: message.content }] : []);
  const parts = visibleParts(rawParts, showToolCalls);
  const lastTextIndex = parts.reduce((acc, p, i) => (p.kind === "text" ? i : acc), -1);

  return (
    <div className="flex flex-col items-start gap-1.5">
      {parts.map((part, i) => {
        if (part.kind === "tool_call") {
          return showToolCalls ? (
            <ToolCallRow key={part.toolCall.id} toolCall={part.toolCall} />
          ) : null;
        }
        if (part.kind === "narration") {
          return <NarrationBubble key={part.narration.id} text={part.narration.text} />;
        }
        return (
          <div
            key={i}
            className={cn(
              "bg-card border-border max-w-[85%] rounded-2xl border px-4 py-2.5 text-sm leading-relaxed",
            )}
          >
            <p className="whitespace-pre-wrap">
              {part.text}
              {i === lastTextIndex && message.streaming ? (
                <span className="animate-pulse">▍</span>
              ) : null}
            </p>
          </div>
        );
      })}
    </div>
  );
}
